/**
 * Gradient Descent Coverage Optimization Algorithm
 *
 * This algorithm uses mathematical optimization to find the best positions for mesh units.
 * It iteratively improves positions by moving in the direction of the coverage gradient,
 * where the gradient is calculated based on achieving multi-frequency coverage targets.
 * It iteratively determines the number of units needed for Basic, Medium, and High coverage targets.
 */
import { WIRELESS_SPECS } from '../constants';
// Import necessary physics/simulation helpers from logic.js
import {
    getMaterialPenetrationLoss,
    createCoverageGrid,
    simulateCoverageGridForFrequency,
    mapFrequenciesToSpeedTiers
} from '../logic';

// Constants for gradient descent
const MAX_ITERATIONS = 50; // Max iterations for optimization *per unit count*
const LEARNING_RATE_INITIAL = 0.5; // Adjusted learning rate for meters
const DECAY_RATE = 0.95;
const GRID_STEP = 10; // Use the smaller grid step from logic.js (now represents meters)
const MAX_UNITS_CAP = 7; // Cap units for performance
const TARGET_COVERAGE_PERCENT = 99; // Target coverage percentage
// Define signal strength thresholds (dBm) for coverage levels
const HIGH_SENSITIVITY_THRESHOLD = -75;
const MEDIUM_SENSITIVITY_THRESHOLD = -80;
const BASIC_SENSITIVITY_THRESHOLD = -85;
const MIN_DISTANCE_BETWEEN_UNITS_METERS = 2.0; // Min distance in meters


// --- Helper Functions ---

const getPrimaryRouterPlacementDescription = (formData) => {
  if (formData.routerLocation === 'livingRoom') return 'Place your primary router in the living room, preferably in a central location and elevated.';
  if (formData.routerLocation === 'office') return 'Place your primary router in the home office, preferably elevated and away from other electronics.';
  if (formData.routerLocation === 'bedroom') return 'Place your primary router in the bedroom, preferably elevated and away from the bed.';
  if (formData.routerLocation === 'kitchen') return 'Place your primary router in the kitchen, but keep it away from appliances like microwaves that can cause interference.';
  if (formData.routerLocation === 'hallway') return 'Place your primary router in the hallway, which provides good central coverage for the surrounding rooms.';
  if (formData.routerLocation === 'basement') return 'Place your primary router in the basement, but be aware that this may limit coverage on upper floors.';
  if (formData.routerLocation === 'custom' && formData.customRouterLocation) return `Place your primary router in ${formData.customRouterLocation}, preferably elevated for better signal propagation.`;
  return 'Place your primary router in a central location in your home, preferably elevated for better signal propagation.';
};

// Removed getFloorPlanDimensions

// **Removed**: createObstaclesFromFormDataMeters - Obstacles now come from floorPlanLayout

// **Modified**: Get primary router location using floorPlanLayout dimensions
const getPrimaryRouterLocation = (formData, floorPlanLayout) => {
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  let xMeters = widthMeters / 2;
  let yMeters = heightMeters / 2;
  if (formData.routerLocation === 'livingRoom') { xMeters *= 1.2; yMeters *= 0.8; }
  else if (formData.routerLocation === 'office') { xMeters *= 0.6; yMeters *= 0.6; }
  else if (formData.routerLocation === 'bedroom') { xMeters *= 1.4; yMeters *= 0.6; }
  else if (formData.routerLocation === 'kitchen') { xMeters *= 0.6; yMeters *= 1.4; }
  return {
      x: Math.max(0, Math.min(widthMeters, xMeters)),
      y: Math.max(0, Math.min(heightMeters, yMeters))
  };
};

// **Modified**: Initialize positions using floorPlanLayout dimensions
const initializePositions = (floorPlanLayout, unitCount, primaryPosMeters) => {
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  const pos = [{ ...primaryPosMeters, type: 'primary' }]; // Start with primary (meters)
  const radius = Math.min(widthMeters, heightMeters) * 0.35; // Radius in meters
  const stepAngle = (unitCount > 1) ? (2 * Math.PI / (unitCount - 1)) : 0;

  for (let i = 1; i < unitCount; i++) {
      const angle = (i - 1) * stepAngle;
      const x = primaryPosMeters.x + radius * Math.cos(angle);
      const y = primaryPosMeters.y + radius * Math.sin(angle);
      // Clamp to layout meter bounds
      pos.push({
          x: Math.max(0, Math.min(widthMeters, x)),
          y: Math.max(0, Math.min(heightMeters, y)),
          type: 'mesh'
      });
  }
  return pos; // Returns positions in meters
};

// **Modified**: Calculate score using meter-based simulation and floorPlanLayout
const calculateCoverageScore = (positions, floorPlanLayout, specs, formData) => {
    // positions are already in meters
    const obstacles = floorPlanLayout.obstacles; // Use layout obstacles

    // Create grids using area (or layout dimensions if createCoverageGrid supports it)
    const aspectRatio = floorPlanLayout.layoutWidthMeters / floorPlanLayout.layoutHeightMeters || 1;
    const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
    const grid5 = createCoverageGrid(formData.area, aspectRatio);
    const grid6 = (specs.BANDS?.includes('6GHz')) ? createCoverageGrid(formData.area, aspectRatio) : null;

    // Define frequency parameters
    const params2_4 = { frequencyGHz: 2.4, transmitPowerDBm: specs.TX_POWER_2_4GHZ || 20, penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType) };
    const params5 = { frequencyGHz: 5, transmitPowerDBm: specs.TX_POWER_5GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(5, formData.wallDensity, formData.constructionType) };
    const params6 = grid6 ? { frequencyGHz: 6, transmitPowerDBm: specs.TX_POWER_6GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(6, formData.wallDensity, formData.constructionType) } : null;

    // Simulate coverage using meter-based positions and layout obstacles
    simulateCoverageGridForFrequency(positions, grid2_4, obstacles, params2_4);
    simulateCoverageGridForFrequency(positions, grid5, obstacles, params5);
    if (grid6 && params6) {
        simulateCoverageGridForFrequency(positions, grid6, obstacles, params6);
    }

    // Map frequencies using the updated grids
    const percentages = mapFrequenciesToSpeedTiers(grid2_4, grid5, grid6, specs);

    // Calculate score based on how close percentages are to the target
    const basicScore = Math.min(1, percentages.basic / TARGET_COVERAGE_PERCENT);
    const mediumScore = Math.min(1, percentages.medium / TARGET_COVERAGE_PERCENT);
    const highScore = Math.min(1, percentages.high / TARGET_COVERAGE_PERCENT);

    // Weighted score
    return (basicScore * 0.4) + (mediumScore * 0.3) + (highScore * 0.3);
};

// **Modified**: Calculate gradient using meter-based simulation, delta, and floorPlanLayout
const calculateCoverageGradient = (positions, idx, floorPlanLayout, specs, formData) => {
  const deltaMeters = 0.1; // Small displacement in meters
  const currentScore = calculateCoverageScore(positions, floorPlanLayout, specs, formData);

  // Calculate score with +delta X displacement (meters)
  const positionsPlusX = positions.map((p, i) => i === idx ? { ...p, x: p.x + deltaMeters } : p);
  const scorePlusX = calculateCoverageScore(positionsPlusX, floorPlanLayout, specs, formData);

  // Calculate score with +delta Y displacement (meters)
  const positionsPlusY = positions.map((p, i) => i === idx ? { ...p, y: p.y + deltaMeters } : p);
  const scorePlusY = calculateCoverageScore(positionsPlusY, floorPlanLayout, specs, formData);

  // Calculate gradient components
  const gradX = (scorePlusX - currentScore) / deltaMeters;
  const gradY = (scorePlusY - currentScore) / deltaMeters;

  // Normalize gradient vector
  const magnitude = Math.sqrt(gradX * gradX + gradY * gradY);
  return (magnitude > 0) ? { x: gradX / magnitude, y: gradY / magnitude } : { x: 0, y: 0 };
};

// **Modified**: Format placements (already in meters)
const formatSolutionPlacements = (positions, formData) => {
    // Positions are already in meters
    return positions.map((pos, index) => ({
        type: pos.type,
        position: { x: pos.x, y: pos.y }, // Store meter coordinates
        description: pos.type === 'primary' ? getPrimaryRouterPlacementDescription(formData) : `Place mesh unit ${index + 1} (Gradient Descent).`,
        tips: []
    }));
};

// --- Exported Functions ---

/**
 * Runs the Gradient Descent algorithm to find the best placement for a GIVEN number of units,
 * keeping the primary router fixed. Uses floorPlanLayout.
 */
export const calculateGradientDescentPlacementsForCount = (formData, routerStandard, routerSpecs, unitCount, floorPlanLayout) => {
  if (unitCount < 1 || !floorPlanLayout) return []; // Check for layout
  unitCount = Math.min(unitCount, MAX_UNITS_CAP);

  // Use dimensions and obstacles from floorPlanLayout
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  // const obstacles = floorPlanLayout.obstacles; // Obstacles used in gradient calc

  // Determine primary location using layout dimensions
  const primaryPos = getPrimaryRouterLocation(formData, floorPlanLayout);
  // Initialize positions using layout dimensions
  let positions = initializePositions(floorPlanLayout, unitCount, primaryPos);

  let currentLearningRate = LEARNING_RATE_INITIAL; // Use adjusted rate for meters
  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    const nextPositions = positions.map((pos, i) => {
        if (pos.type === 'primary') return pos; // Don't move the primary router

        // Calculate gradient using floorPlanLayout
        const gradient = calculateCoverageGradient(positions, i, floorPlanLayout, routerSpecs, formData);

        // Update position based on gradient and learning rate (meters)
        let newX = pos.x + currentLearningRate * gradient.x;
        let newY = pos.y + currentLearningRate * gradient.y;
        // Clamp to layout meter bounds
        newX = Math.max(0, Math.min(widthMeters, newX));
        newY = Math.max(0, Math.min(heightMeters, newY));

        // Basic collision avoidance (using meter distance)
        for(let j=0; j < positions.length; j++) {
            if (i === j) continue;
            const otherPos = positions[j];
            const dist = Math.sqrt(Math.pow(newX - otherPos.x, 2) + Math.pow(newY - otherPos.y, 2)); // meters
            if (dist < MIN_DISTANCE_BETWEEN_UNITS_METERS) {
                const pushFactor = (MIN_DISTANCE_BETWEEN_UNITS_METERS - dist) / (dist + 1e-6) * 0.5; // Avoid division by zero
                newX += (newX - otherPos.x) * pushFactor;
                newY += (newY - otherPos.y) * pushFactor;
                // Re-clamp after push
                newX = Math.max(0, Math.min(widthMeters, newX));
                newY = Math.max(0, Math.min(heightMeters, newY));
            }
        }
        return { ...pos, x: newX, y: newY };
    });

    // Convergence check could be added here based on change in position (meters)

    positions = nextPositions; // Update positions (meters)
    currentLearningRate *= DECAY_RATE; // Decay learning rate
  }
  return formatSolutionPlacements(positions, formData); // Format meter positions
};

/**
 * Calculates coverage percentages based on the final optimized positions using physics simulation
 * and the provided floorPlanLayout.
 */
export const calculateGradientDescentCoveragePercentage = (unitCount, placements, formData, routerSpecs, floorPlanLayout) => {
    if (!floorPlanLayout || !placements || placements.length === 0) {
        return { basic: 0, medium: 0, high: 0 };
    }
    // Use dimensions and obstacles from floorPlanLayout
    const widthMeters = floorPlanLayout.layoutWidthMeters;
    const heightMeters = floorPlanLayout.layoutHeightMeters;
    const obstacles = floorPlanLayout.obstacles; // Meter coords

    // Extract positions (already in meters from placements)
    const positions = placements.map(p => ({...p.position, type: p.type})); // Meter coords

    // Create grids using area (or layout dimensions if createCoverageGrid supports it)
    const aspectRatio = widthMeters / heightMeters || 1;
    const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
    const grid5 = createCoverageGrid(formData.area, aspectRatio);
    const grid6 = (routerSpecs.BANDS?.includes('6GHz')) ? createCoverageGrid(formData.area, aspectRatio) : null;

    // Define frequency parameters
    const params2_4 = { frequencyGHz: 2.4, transmitPowerDBm: routerSpecs.TX_POWER_2_4GHZ || 20, penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType) };
    const params5 = { frequencyGHz: 5, transmitPowerDBm: routerSpecs.TX_POWER_5GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(5, formData.wallDensity, formData.constructionType) };
    const params6 = grid6 ? { frequencyGHz: 6, transmitPowerDBm: routerSpecs.TX_POWER_6GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(6, formData.wallDensity, formData.constructionType) } : null;

    // Simulate coverage using meter-based positions and layout obstacles
    simulateCoverageGridForFrequency(positions, grid2_4, obstacles, params2_4);
    simulateCoverageGridForFrequency(positions, grid5, obstacles, params5);
    if (grid6 && params6) {
        simulateCoverageGridForFrequency(positions, grid6, obstacles, params6);
    }

    // Map frequencies using the updated grids
    return mapFrequenciesToSpeedTiers(grid2_4, grid5, grid6, routerSpecs);
};


/**
 * Main function for the Gradient Descent Algorithm.
 * Iteratively determines the number of units required to meet coverage targets.
 * Uses the provided floorPlanLayout.
 */
const gradientDescentAlgorithm = (formData, routerStandard, routerSpecs, floorPlanLayout) => {
  // Check if floorPlanLayout is provided
  if (!floorPlanLayout) {
      console.error("Gradient Descent Algorithm requires floorPlanLayout.");
      // Return a default error structure or throw an error
      return {
          coverageLevels: { basic: { units: 0, placements: [] }, medium: { units: 0, placements: [] }, high: { units: 0, placements: [] } },
          coveragePercentages: { basic: 0, medium: 0, high: 0 }
      };
  }

  let currentUnitCount = 0;
  let placements = []; // Will store meter-based placements
  let currentPercentages = { basic: 0, medium: 0, high: 0 };

  let basicResult = null;
  let mediumResult = null;
  let highResult = null;

  console.log(`GradientDescent: Starting iterative calculation (using meters)...`);

  while (currentUnitCount < MAX_UNITS_CAP) {
    currentUnitCount++;
    console.log(`GradientDescent: Optimizing for ${currentUnitCount} units...`);

    // Run Gradient Descent to find the best placements using floorPlanLayout
    placements = calculateGradientDescentPlacementsForCount(formData, routerStandard, routerSpecs, currentUnitCount, floorPlanLayout);

    // Calculate coverage percentages using floorPlanLayout
    currentPercentages = calculateGradientDescentCoveragePercentage(currentUnitCount, placements, formData, routerSpecs, floorPlanLayout);
    console.log(`GradientDescent: ${currentUnitCount} units -> B:${currentPercentages.basic}%, M:${currentPercentages.medium}%, H:${currentPercentages.high}%`);

    // Check if targets met and store the first time they are met
    if (!basicResult && currentPercentages.basic >= TARGET_COVERAGE_PERCENT) {
      console.log(`GradientDescent: Basic target met at ${currentUnitCount} units.`);
      basicResult = { units: currentUnitCount, placements: [...placements], coverage: {...currentPercentages} }; // Store meter placements
    }
    if (!mediumResult && currentPercentages.medium >= TARGET_COVERAGE_PERCENT) {
      console.log(`GradientDescent: Medium target met at ${currentUnitCount} units.`);
      mediumResult = { units: currentUnitCount, placements: [...placements], coverage: {...currentPercentages} }; // Store meter placements
    }
    if (!highResult && currentPercentages.high >= TARGET_COVERAGE_PERCENT) {
      console.log(`GradientDescent: High target met at ${currentUnitCount} units.`);
      highResult = { units: currentUnitCount, placements: [...placements], coverage: {...currentPercentages} }; // Store meter placements
    }

    // Exit loop if all targets are met
    if (basicResult && mediumResult && highResult) {
       console.log(`GradientDescent: All targets met.`);
       break;
    }
  }

   // If targets weren't met after reaching the cap, use the results from the max unit count achieved
   const finalResult = { units: currentUnitCount, placements: [...placements], coverage: {...currentPercentages} }; // Use final meter placements
   if (!basicResult) { console.log(`GradientDescent: Basic target not met, using max units (${currentUnitCount}).`); basicResult = finalResult; }
   if (!mediumResult) { console.log(`GradientDescent: Medium target not met, using max units (${currentUnitCount}).`); mediumResult = finalResult; }
   if (!highResult) { console.log(`GradientDescent: High target not met, using max units (${currentUnitCount}).`); highResult = finalResult; }

  // Return results based on when each target was first met, or the final state if not met
  return {
    coverageLevels: {
      basic: { units: basicResult.units, placements: basicResult.placements }, // Meter placements
      medium: { units: mediumResult.units, placements: mediumResult.placements }, // Meter placements
      high: { units: highResult.units, placements: highResult.placements } // Meter placements
    },
    coveragePercentages: { // Report the coverage achieved *at the point the target was met* or the final coverage if not met
        basic: basicResult.coverage.basic,
        medium: mediumResult.coverage.medium,
        high: highResult.coverage.high
    }
  };
};

export default gradientDescentAlgorithm;